import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Avatar,
  useTheme,
  Skeleton
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import InsightCard from './InsightCard';

interface Divergencia {
  descricao: string;
}

interface TipoDivergencia {
  quantidade: number;
  divergencias: Divergencia[];
}

interface DivergenciasProps {
  insight: string | null;
  insightLoading: boolean;
  divergencias: Record<string, TipoDivergencia>;
  loading?: boolean;
}

const Divergencias: React.FC<DivergenciasProps> = ({
  insight,
  insightLoading,
  divergencias,
  loading = false
}) => {
  const theme = useTheme();

  const renderSkeletonCards = () => {
    return (
      <>
        {/* Skeleton do Insight Card (9 colunas) */}
        <Grid item xs={12} md={9}>
          <Box sx={{ height: '480px' }}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Skeleton variant="circular" width={36} height={36} sx={{ mr: 1 }} />
                <Skeleton variant="text" width="200px" height={24} />
              </Box>
              <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
                <Table size="small">
                  <TableBody>
                    {Array.from({ length: 5 }, (_, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ border: 0 }}>
                          <Skeleton variant="text" width="100%" height={16} />
                          <Skeleton variant="text" width="80%" height={16} sx={{ mt: 1 }} />
                          <Skeleton variant="text" width="90%" height={16} sx={{ mt: 1 }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>
          </Box>
        </Grid>

        {/* Skeleton do card IA (3 colunas) */}
        <Grid item xs={12} md={3}>
          <Box sx={{ height: '480px', borderRadius: '3px' }}>
            <Card sx={{
              borderRadius: 4,
              height: '100%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box>
                <Box display="flex" alignItems="center" mb={3}>
                  <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2, bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                  <Skeleton variant="text" width="140px" height={28} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                </Box>
                <Skeleton variant="text" width="100%" height={16} sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                <Skeleton variant="text" width="100%" height={16} sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                <Skeleton variant="text" width="100%" height={16} sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                <Skeleton variant="text" width="100%" height={16} sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
              </Box>
              <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2 }}>
                <Skeleton variant="text" width="100%" height={12} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                <Skeleton variant="text" width="90%" height={12} sx={{ mt: 1, bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
              </Box>
            </CardContent>
          </Card>
          </Box>
        </Grid>

        {/* Skeletons dos cards de divergências */}
        {Array.from({ length: 2 }, (_, index) => (
          <Grid item xs={12} sm={6} md={6} lg={6} key={`skeleton-divergencia-${index}`}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Skeleton variant="circular" width={32} height={32} sx={{ mr: 1 }} />
                  <Skeleton variant="text" width="60%" height={20} />
                </Box>

                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Skeleton variant="text" width="80px" height={16} />
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.from({ length: 3 }, (_, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Skeleton variant="text" width="100%" height={14} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </>
    );
  };

  return (
    <Grid container spacing={2}>
      {(insightLoading || insight) && (
        <>
          <Grid item xs={12} md={9}>
            <Box sx={{ height: '480px' }}>
              <InsightCard
                title="Insight Gerado"
                content={insightLoading ? '' : insight || ''}
                loading={insightLoading}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ height: '480px' }}>
              <Card sx={{
                borderRadius: 4,
                height: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  zIndex: 1
                }
              }}>
              <CardContent sx={{
                p: 3,
                position: 'relative',
                zIndex: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                borderRadius: 4
              }}>
                <Box>
                  <Box display="flex" alignItems="center" mb={3}>
                    <Avatar
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        width: 48,
                        height: 48,
                        mr: 2,
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      🤖
                    </Avatar>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                      Inteligência Artificial
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.6, mb: 2 }}>
                    Análises realizadas por IA avançada que processa dados fiscais e identifica padrões, tendências e oportunidades de negócio.
                  </Typography>

                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.6, mb: 2 }}>
                    📊 <strong>Análise de dados:</strong> Processamento inteligente de informações fiscais
                  </Typography>

                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.6, mb: 2 }}>
                    🎯 <strong>Insights relevantes:</strong> Identificação de necessidades e oportunidades
                  </Typography>

                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.6 }}>
                    📈 <strong>Otimização:</strong> Sugestões para melhoria de processos
                  </Typography>
                </Box>

                <Box sx={{
                  mt: 1.5,
                  p: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  backdropFilter: 'blur(10px)'
                }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontStyle: 'italic' }}>
                    ⚠️ <strong>Disclaimer:</strong> A IA pode cometer erros. Sempre valide as informações com dados oficiais e consulte profissionais especializados.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
            </Box>
          </Grid>
        </>
      )}

      {loading ? (
        renderSkeletonCards()
      ) : (
        Object.entries(divergencias).map(
          ([tipo, { quantidade, divergencias }], index, array) => {
            const colSize = array.length === 1 ? 12 : 6;

            return (
              <Grid item xs={12} sm={colSize} md={colSize} lg={colSize} key={tipo}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.warning.light,
                          color: theme.palette.warning.main,
                          width: 32,
                          height: 32,
                          mr: 1
                        }}
                      >
                        <WarningIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="subtitle1" sx={{ color: theme.palette.text.secondary }}>
                        {quantidade} Divergência{quantidade !== 1 && 's'} de {tipo}
                      </Typography>
                    </Box>

                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                Descrição
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {divergencias.length > 0 ? (
                            divergencias.map((div, i) => (
                              <TableRow key={i}>
                                <TableCell>{div.descricao}</TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell>
                                Nenhuma divergência de {tipo} encontrada.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          }
        )
      )}
    </Grid>
  );
};

export default Divergencias;
